import { CalendarOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { Button, Card, Col, Empty, Radio, Row, Spin } from "antd";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EventOccasionsActions } from "../components/feature/EventOccasionsActions";
import { EventOccasionsFilterTabs } from "../components/feature/EventOccasionsFilterTabs";
import { EventOccasionsTableView } from "../components/feature/EventOccasionsTableView";
import { EventOccasionCard } from "../components/ui/EventOccasionCard";
import type { DisplayFilter, EventOccasion, ViewMode } from "../types";

export interface EventOccasionsListPageProps {
	occasions?: EventOccasion[];
	loading?: boolean;
	hasMore?: boolean;
	loadMore?: () => void;
	isFetchingNextPage?: boolean;
	primaryColor?: string;
	primaryLightColor?: string;
	imageBaseUrl?: string;
	onEventClick?: (occasion: EventOccasion) => void;
	eventListUrl?: string;
	showDetailedTable?: boolean;
	onVisitRecord?: () => void;
	onRegisterEvent?: () => void;
	onCreateEvent?: () => void;
	onEditDisplayOrder?: () => void;
	onDownloadCSV?: () => void;
	displayTab?: DisplayFilter;
	onTabChange?: (tab: DisplayFilter) => void;
}

export const EventOccasionsListPage: React.FC<EventOccasionsListPageProps> = ({
	occasions = [],
	loading = false,
	hasMore = false,
	loadMore,
	isFetchingNextPage = false,
	primaryColor = "#99CA29",
	primaryLightColor = "#fdfff0",
	imageBaseUrl = "",
	onEventClick,
	eventListUrl,
	showDetailedTable = false,
	onVisitRecord,
	onRegisterEvent,
	onCreateEvent,
	onEditDisplayOrder,
	onDownloadCSV,
	displayTab: propsDisplayTab,
	onTabChange,
}) => {
	const [viewMode, setViewMode] = useState<ViewMode>("calendar");
	const displayTab = propsDisplayTab ?? "displayed";
	const loadMoreRef = useRef<HTMLDivElement>(null);

	const handleLoadMore = useCallback(() => {
		if (!loading && !isFetchingNextPage && hasMore && loadMore) {
			loadMore();
		}
	}, [loading, isFetchingNextPage, hasMore, loadMore]);

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					handleLoadMore();
				}
			},
			{ threshold: 0.1 },
		);

		const currentRef = loadMoreRef.current;
		if (currentRef) {
			observer.observe(currentRef);
		}

		return () => {
			if (currentRef) {
				observer.unobserve(currentRef);
			}
		};
	}, [handleLoadMore]);

	const filteredOccasions = useMemo(() => {
		if (displayTab === "displayed") {
			return occasions.filter((occasion) => occasion.isDisplayed);
		}
		return occasions.filter((occasion) => !occasion.isDisplayed);
	}, [occasions, displayTab]);

	return (
		<div style={{ padding: "24px" }}>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: "16px",
				}}
			>
				<h2 style={{ margin: 0, fontSize: "20px", fontWeight: "bold" }}>
					イベント一覧
				</h2>
				<EventOccasionsActions
					primaryColor={primaryColor}
					onVisitRecord={onVisitRecord}
					onRegisterEvent={onRegisterEvent}
					onCreateEvent={onCreateEvent}
					onEditDisplayOrder={onEditDisplayOrder}
					onDownloadCSV={onDownloadCSV}
				/>
			</div>

			<Row gutter={[32, 16]}>
				<Col xl={24} lg={24} md={24} xs={24}>
					<div
						style={{
							border: `1px solid ${primaryColor}`,
							borderRadius: "4px",
							height: "100%",
						}}
					>
						<Card variant="borderless">
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: "16px",
								}}
							>
								<EventOccasionsFilterTabs
									activeTab={displayTab}
									onTabChange={onTabChange || (() => {})}
									eventListUrl={eventListUrl}
									primaryColor={primaryColor}
									showCopyButton={false}
								/>
								<Radio.Group
									value={viewMode}
									onChange={(e) => setViewMode(e.target.value)}
								>
									<Radio.Button value="calendar">
										<CalendarOutlined />
									</Radio.Button>
									<Radio.Button value="table">
										<UnorderedListOutlined />
									</Radio.Button>
								</Radio.Group>
							</div>
							{viewMode === "table" ? (
								<EventOccasionsTableView
									occasions={filteredOccasions}
									loading={loading}
									imageBaseUrl={imageBaseUrl}
									primaryColor={primaryColor}
									onEventClick={onEventClick}
									showDetailedColumns={showDetailedTable}
								/>
							) : (
								<>
									<Row gutter={[16, 16]}>
										{filteredOccasions && filteredOccasions.length > 0 ? (
											filteredOccasions.map((occasion) => (
												<Col
													xs={24}
													lg={12}
													xxl={8}
													key={occasion.id}
													style={{ width: "100%" }}
												>
													<EventOccasionCard
														event={occasion}
														primaryColor={primaryColor}
														primaryLightColor={primaryLightColor}
														imageBaseUrl={imageBaseUrl}
														onClick={() => onEventClick?.(occasion)}
													/>
												</Col>
											))
										) : (
											<div
												style={{
													display: "flex",
													justifyContent: "center",
													width: "100%",
												}}
											>
												<Empty description="イベントプログラムがありません。" />
											</div>
										)}
									</Row>
									<div
										ref={loadMoreRef}
										style={{
											display: "flex",
											justifyContent: "center",
											marginTop: "16px",
										}}
									>
										{isFetchingNextPage && <Spin />}
										{hasMore && !isFetchingNextPage && (
											<Button onClick={handleLoadMore}>もっと見る</Button>
										)}
									</div>
								</>
							)}
						</Card>
					</div>
				</Col>
			</Row>
		</div>
	);
};
